import EventEmitter from 'wolfy87-eventemitter';
import Utils from './Utils';

let _cache = {
  products: {},
  brands: {},
  cart: null,
  cartVisible: false,
  user: null,
  orderList: null,
  retailer: null,
  lastSelectedRetailerToken: null,
  recommendations: {
    url: null,
    response: null
  },
  categoryTypes: null,
  categories: {},
  productCollections: null,

  productSearchActive: false,
  productSearchTerm: '',
  productSearchRecommendations: null,

  selectedInstrument: '',
  lastAddedProductToken: null,
  adminBarVisible: true,
  campaignRetailerToken: null,

  currentRecommendationSet: null,
  currentRecommendationFilter: []
};

let cache = {
  emitter: new EventEmitter(),
  eventTypes: {
    CATEGORIES_CHANGED: 'CATEGORIES_CHANGED',
    CATEGORY_DATA_CHANGED: 'CATEGORY_DATA_CHANGED',
    PRODUCTS_CHANGED: 'PRODUCTS_CHANGED',
    BRANDS_CHANGED: 'BRANDS_CHANGED',
    RECOMMENDATIONS_CHANGED: 'RECOMMENDATIONS_CHANGED',
    CART_CHANGED: 'CART_CHANGED',
    CART_VISIBLE_CHANGED: 'CART_VISIBLE_CHANGED',
    USER_CHANGED: 'USER_CHANGED',
    RETAILER_CHANGED: 'RETAILER_CHANGED',
    ORDER_LIST_CHANGED: 'ORDER_LIST_CHANGED',
    PRODUCT_COLLECTIONS_CHANGED: 'PRODUCT_COLLECTIONS_CHANGED',
    PRODUCT_SEARCH_TERM_CHANGED: 'PRODUCT_SEARCH_TERM_CHANGED',
    PRODUCT_SEARCH_ACTIVE_CHANGED: 'PRODUCT_SEARCH_ACTIVE_CHANGED',
    PRODUCT_SEARCH_RECOMMENDATIONS_CHANGED: 'PRODUCT_SEARCH_RECOMMENDATIONS_CHANGED',
    SELECTED_INSTRUMENT_CHANGED: 'SELECTED_INSTRUMENT_CHANGED',
    LAST_ADDED_PRODUCT_TOKEN_CHANGED: 'LAST_ADDED_PRODUCT_TOKEN_CHANGED',
    ADMIN_BAR_VISIBILITY_CHANGED: 'ADMIN_BAR_VISIBILITY_CHANGED',
    CURRENT_RECOMMENDATION_SET_CHANGED: 'CURRENT_RECOMMENDATION_SET_CHANGED',
    CURRENT_RECOMMENDATION_FILTER_CHANGED: 'CURRENT_RECOMMENDATION_FILTER_CHANGED'
  },

  addChangeListener(callback, eventType)  {
    this.emitter.on(eventType, callback);
  },

  removeChangeListener(callback, eventType) {
    this.emitter.removeListener(eventType, callback);
  },

  // Get the cached value based on the eventType
  getForEventType(eventType) {
    switch (eventType) {
      case this.eventTypes.USER_CHANGED:
        return this.getUser();
      case this.eventTypes.RETAILER_CHANGED:
        return this.getRetailer();
      case this.eventTypes.CART_CHANGED:
        return this.getCart();
      case this.eventTypes.CART_VISIBLE_CHANGED:
        return this.getCartVisible();
      case this.eventTypes.ORDER_LIST_CHANGED:
        return this.getOrderList();
      case this.eventTypes.PRODUCT_SEARCH_TERM_CHANGED:
        return this.getProductSearchTerm();
      case this.eventTypes.PRODUCT_SEARCH_ACTIVE_CHANGED:
        return this.getProductSearchActive();
      case this.eventTypes.PRODUCT_SEARCH_RECOMMENDATIONS_CHANGED:
        return this.getProductSearchRecommendations();
      case this.eventTypes.RECOMMENDATIONS_CHANGED:
        return this.getRecommendations().response;
      case this.eventTypes.PRODUCT_COLLECTIONS_CHANGED:
        return this.getProductCollections();
      case this.eventTypes.LAST_ADDED_PRODUCT_TOKEN_CHANGED:
        return this.getLastAddedProductToken();
      case this.eventTypes.ADMIN_BAR_VISIBILITY_CHANGED:
        return this.getAdminBarVisible();
      case this.eventTypes.CATEGORIES_CHANGED:
        return this.getCategories();
      case this.eventTypes.CATEGORY_DATA_CHANGED:
        return this.getCategoryData();
      case this.eventTypes.CURRENT_RECOMMENDATION_SET_CHANGED:
        return this.getCurrentRecommendationSet();
      case this.eventTypes.CURRENT_RECOMMENDATION_FILTER_CHANGED:
        return this.getCurrentRecommendationFilter();
      default:
        console.error('getEventForType() does not have a value for the type "%s"', eventType);
    }

    return null;
  },

  /* --- Recommendations --- */

  setRecommendations(url, response) {
    _cache.recommendations.url = url;
    _cache.recommendations.response = response;
    this.emitter.emitEvent(this.eventTypes.RECOMMENDATIONS_CHANGED);
  },

  getRecommendations() {
    return _cache.recommendations;
  },

  hasProductRecommendations() {
    if (!_cache.recommendations.response || !_cache.recommendations.response.products) return false;
    if (Array.isArray(_cache.recommendations.response.products) && _cache.recommendations.response.products.length > 0) return true;
    if (_cache.recommendations.response.display_order && _cache.recommendations.response.display_order.length > 0) return true;

    return false;
  },

  /* --- Products --- */

  setProducts(products) {
    if (Array.isArray(products)) {
      products.forEach((product) => {
        _cache.products[product.token] = product;
      });
    } else {
      Object.keys(products).forEach(token => {
        _cache.products[token] = products[token];
      });
    }

    this.emitter.emitEvent(this.eventTypes.PRODUCTS_CHANGED);
  },

  removeProduct(productToken) {
    delete _cache.products[productToken];
    this.emitter.emitEvent(this.eventTypes.PRODUCTS_CHANGED);
  },

  /* --- Brand --- */

  setBrands(brands) {
    if (Array.isArray(brands)) {
      brands.forEach((brand) => {
        _cache.brands[brand.token] = brand;
      });
    } else {
      Object.keys(brands).forEach((brandToken) => {
        _cache.brands[brandToken] = brands[brandToken];
      });
    }
    this.emitter.emitEvent(this.eventTypes.BRANDS_CHANGED);
  },

  getBrand(brandToken) {
    return (_cache.brands[brandToken]);
  },

  removeBrand(brandToken) {
    delete _cache.brands[brandToken];
    this.emitter.emitEvent(this.eventTypes.BRANDS_CHANGED);
  },

  /* --- Categories --- */
  setCategories(data) {
    if (_cache.categoryTypes === data) return;

    _cache.categoryTypes = data;
    this.emitter.emitEvent(this.eventTypes.CATEGORIES_CHANGED);
  },

  getCategories() {
    return _cache.categoryTypes;
  },

  clearCategoryData() {
    _cache.categories = {};
  },

  setCategoryData(query, data) {
    _cache.categories[query] = data;
    this.emitter.emitEvent(this.eventTypes.CATEGORY_DATA_CHANGED);
  },

  getCategoryData(query) {
    return _cache.categories[query];
  },

  /* --- Cart --- */
  setCart(data) {
    // Only change the data if the data is different
    if (_cache.cart === data) return;

    data = data || {};

    let i, j;

    let parsed_items = [];
    let cart_value_cents = 0;
    let below_minimum = [];

    // Find all items with the given brand_token.
    function itemsForBrand(items, brand_token) {
      return items.filter(function (item) {
        return item.brand_token === brand_token;
      });
    }

    let brandKeys = data.brands ? Object.keys(data.brands) : [];
    for (i = 0; i < brandKeys.length; i++) {
      let brand = Utils.clone(data.brands[brandKeys[i]]);

      // Put all items from our brand in our new object
      brand.products = itemsForBrand(data.cart.items, brand.token);

      // Calculate the total order value for the brand
      brand.total_cents = 0;
      for (j = 0; j < brand.products.length; j++) {
        let currentItem = brand.products[j];
        try {
          brand.total_cents += currentItem.price_cents * currentItem.quantity;
        } catch (e) {
          console.error(e);
          console.error("Error adding total cents.");
          console.error(currentItem);
          console.error("brand");
          console.error(brand);
        }
      }
      brand.below_minimum = (brand.total_cents < brand.minimum_order_amount_cents);
      if (brand.below_minimum) {
        below_minimum.push(brand.token);
      }
      cart_value_cents += brand.total_cents;

      parsed_items.push(brand);
    }

    data.cart = data.cart || {items: []};
    data.parsed_items = parsed_items;
    data.cart.total_value_cents = cart_value_cents;
    data.below_minimum = below_minimum;

    _cache.cart = data;
    this.emitter.emitEvent(this.eventTypes.CART_CHANGED);
  },

  getCart() {
    return _cache.cart;
  },

  setCartVisible(visible) {
    if (visible === _cache.cartVisible) return;
    _cache.cartVisible = visible;
    this.emitter.emitEvent(this.eventTypes.CART_VISIBLE_CHANGED);
  },

  getCartVisible() {
    return _cache.cartVisible;
  },

  /* --- Last Added Product --- */
  getLastAddedProductToken() {
    return (_cache.lastAddedProductToken);
  },

  setLastAddedProductToken(productToken) {
    if (_cache.lastAddedProductToken === productToken) return;

    _cache.lastAddedProductToken = productToken;
    this.emitter.emitEvent(this.eventTypes.LAST_ADDED_PRODUCT_TOKEN_CHANGED);
  },

  /* --- User --- */
  setUser(user) {
    if (_cache.user === user) return;

    // Clear category cache whenever user changes.
    if (!_cache.user || !user) this.clearCategoryData();
    if (_cache.user && user && _cache.user.token !== user.token) this.clearCategoryData();

    _cache.user = user;
    this.emitter.emitEvent(this.eventTypes.USER_CHANGED);
  },

  getUser() {
    return _cache.user;
  },

  /* --- Retailer --- */
  setRetailer(retailer) {
    if (_cache.retailer === retailer) return;

    _cache.retailer = retailer;
    if (retailer && retailer.token && retailer.token !== _cache.lastSelectedRetailerToken) {
      _cache.lastSelectedRetailerToken = retailer.token;
      this.clearCategoryData();
    }
    if (!retailer) {
      this.clearCategoryData();
    }

    this.emitter.emitEvent(this.eventTypes.RETAILER_CHANGED);
  },

  getRetailer() {
    return _cache.retailer;
  },

  setLastSelectedRetailerToken(token) {
    _cache.lastSelectedRetailerToken = token;
  },

  getLastSelectedRetailerToken() {
    return _cache.lastSelectedRetailerToken;
  },

  /* --- Order List --- */
  setOrderList(list) {
    _cache.orderList = list;
    this.emitter.emitEvent(this.eventTypes.ORDER_LIST_CHANGED);
  },

  getOrderList() {
    return _cache.orderList;
  },

  /* --- Product Collections --- */
  setProductCollections(productCollections) {
    _cache.productCollections = productCollections;
    this.emitter.emitEvent(this.eventTypes.PRODUCT_COLLECTIONS_CHANGED);
  },

  getProductCollections() {
    return _cache.productCollections;
  },

  /* --- Search --- */
  setProductSearchTerm(productSearchTerm) {
    if (_cache.productSearchTerm === productSearchTerm) return;

    _cache.productSearchTerm = productSearchTerm;
    this.emitter.emitEvent(this.eventTypes.PRODUCT_SEARCH_TERM_CHANGED);
  },

  getProductSearchTerm() {
    return _cache.productSearchTerm;
  },

  setProductSearchActive(productSearchActive) {
    if (_cache.productSearchActive === productSearchActive) return;

    _cache.productSearchActive = productSearchActive;
    this.emitter.emitEvent(this.eventTypes.PRODUCT_SEARCH_ACTIVE_CHANGED);
  },

  getProductSearchActive() {
    return _cache.productSearchActive;
  },

  setProductSearchRecommendations(recommendations) {
    if (_cache.productSearchRecommendations === recommendations) return;

    _cache.productSearchRecommendations = recommendations;
    this.emitter.emitEvent(this.eventTypes.PRODUCT_SEARCH_RECOMMENDATIONS_CHANGED);
  },

  getProductSearchRecommendations() {
    return _cache.productSearchRecommendations;
  },

  /* --- Selected Instrument --- */
  setSelectedInstrument(selectedInstrument) {
    if (_cache.selectedInstrument === selectedInstrument) return;

    _cache.selectedInstrument = selectedInstrument;
    this.emitter.emitEvent(this.eventTypes.SELECTED_INSTRUMENT_CHANGED);
  },

  getSelectedInstrument() {
    return _cache.selectedInstrument;
  },

  /* --- Admin Bar --- */
  getAdminBarVisible() {
    return _cache.adminBarVisible;
  },

  setAdminBarVisible(visibility) {
    if (_cache.adminBarVisible === visibility) return;

    _cache.adminBarVisible = visibility;
    this.emitter.emitEvent(this.eventTypes.ADMIN_BAR_VISIBILITY_CHANGED);
  },

  /* --- Campaign Retailer Token --- */
  getCampaignRetailerToken() {
    return _cache.campaignRetailerToken;
  },

  setCampaignRetailerToken(retailerToken) {
    _cache.campaignRetailerToken = retailerToken;
  },

  /* --- Recommendation and Recommendation Filter --- */
  getCurrentRecommendationSet() {
    return _cache.currentRecommendationSet;
  },

  setCurrentRecommendationSet(recommendations) {
    if (_cache.currentRecommendationSet === recommendations) return;

    _cache.currentRecommendationSet = recommendations;
    this.emitter.emitEvent(this.eventTypes.CURRENT_RECOMMENDATION_SET_CHANGED);
  },

  getCurrentRecommendationFilter() {
    return _cache.currentRecommendationFilter;
  },

  setCurrentRecommendationFilter(filter) {
    filter = filter || [];
    if (_cache.currentRecommendationFilter === filter) return;

    _cache.currentRecommendationFilter = filter;
    this.emitter.emitEvent(this.eventTypes.CURRENT_RECOMMENDATION_FILTER_CHANGED);
  }
};

export default cache;
export {_cache};



/** WEBPACK FOOTER **
 ** ./src/Api/cache.js
 **/